import type { ReactNode } from 'react';
import { useStore, type PanelId } from '../../state/store.ts';

interface PanelProps {
  id: PanelId;
  title: string;
  /** Shows the ember dot and enables Reset when the panel holds a change. */
  active?: boolean;
  children: ReactNode;
}

export function Panel({ id, title, active = false, children }: PanelProps) {
  const open = useStore((state) => state.openPanels[id]);
  const togglePanel = useStore((state) => state.togglePanel);
  const resetPanel = useStore((state) => state.resetPanel);

  return (
    <section className={`panel${open ? ' open' : ''}`}>
      <button
        type="button"
        className="panel-head"
        onClick={() => togglePanel(id)}
        aria-expanded={open}
        aria-controls={`panel-${id}`}
      >
        <span className="panel-chevron" aria-hidden="true" />
        <span className="panel-title">{title}</span>
        {active ? (
          <>
            <span
              className="panel-reset"
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                resetPanel(id);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.stopPropagation();
                  event.preventDefault();
                  resetPanel(id);
                }
              }}
            >
              Reset
            </span>
            <span className="panel-dot" aria-label="This panel has changes" />
          </>
        ) : null}
      </button>
      {open ? (
        <div className="panel-body" id={`panel-${id}`}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

export function GroupLabel({ children }: { children: ReactNode }) {
  return <div className="group-label">{children}</div>;
}

interface SegmentedProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  ariaLabel?: string;
}

export function Segmented<T extends string>({ value, options, onChange, ariaLabel }: SegmentedProps<T>) {
  return (
    <div className="segmented" role="tablist" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={option.value === value ? 'on' : ''}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
